/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch } from 'react-redux';
import type { MenuItemType } from '@opengeoweb/shared';
import { ExitDomain } from '@opengeoweb/theme';
import { warningsFormActions } from '../../store';
import type { DrawingListItem } from '../../store/warningsDrawings/types';
import { warningsEventFormActions } from '../../store/warningsEventForm';
import type {
  PublicWarningDetail,
  WarningEvent,
  WarningType,
} from '../../store/warningsForm/types';
import { getWarningsFeatures } from '../../utils/warningsFeatures';

export const BUTTON_AIRMET = 'AIRMET';
export const BUTTON_SIGMET = 'SIGMET';

export type ShareDrawing = (
  drawing: DrawingListItem,
  type: WarningType,
) => void;

/**
 * Shares a stored drawing with a warning form: it opens the form for the given
 * warning type, prefilled with the drawing. Used from every place where a
 * drawing can be shared, so sharing always results in the same form.
 * @returns callback which shares a drawing with a warning form
 */
export const useShareDrawing = (): ShareDrawing => {
  const dispatch = useDispatch();

  return React.useCallback<ShareDrawing>(
    (drawing, type) => {
      if (type === 'warningsEvent') {
        dispatch(
          warningsEventFormActions.openWarningsEventFormDialog({
            event: {
              selectedDrawing: drawing,
            } as WarningEvent,
            formState: 'edit',
          }),
        );
        return;
      }

      dispatch(
        warningsFormActions.openPublicWarningFormDialog({
          object: drawing,
          warning: {
            warningDetail: {
              areas: [
                { geoJSON: drawing.geoJSON, objectName: drawing.objectName },
              ],
            } as PublicWarningDetail,
            type: 'public',
          },
        }),
      );
    },
    [dispatch],
  );
};

/**
 * Builds the menu items for sharing a drawing with a warning form, so every
 * share menu offers the same options.
 * @param t translation function
 * @param onShare callback for the chosen warning type
 * @returns share menu items
 */
export const getShareDrawingMenuItems = (
  t: (key: string) => string,
  onShare: (type: WarningType) => void,
): MenuItemType[] => {
  const {
    hasPublicWarnings: showPublicWarningOption,
    hasWarningEvents: showWarningsEventsOption,
  } = getWarningsFeatures();

  return [
    ...(showPublicWarningOption
      ? [
          {
            text: t('public-warning-title').toUpperCase(),
            action: (): void => {
              onShare('public');
            },
            icon: <ExitDomain />,
          },
        ]
      : []),
    ...(showWarningsEventsOption
      ? [
          {
            text: t('public-warning-event-title').toUpperCase(),
            action: (): void => {
              onShare('warningsEvent');
            },
            icon: <ExitDomain />,
          },
        ]
      : []),
    // sharing with an AIRMET or SIGMET is not supported yet
    {
      text: BUTTON_AIRMET,
      action: (): void => {},
      icon: <ExitDomain />,
      isDisabled: true,
    },
    {
      text: BUTTON_SIGMET,
      action: (): void => {},
      icon: <ExitDomain />,
      isDisabled: true,
    },
  ];
};
